import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Public & Auth Imports
import LandingPage from './pages/index';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BookDiscovery from './pages/BookDiscovery';
import Tier3Inquiry from './pages/Tier3Inquiry';
import AiRiskGrader from './pages/AiRiskGrader';

// Layout & Component Imports
import DashboardLayout from './components/DashboardLayout';
import ComplianceChatbot from './components/ComplianceChatbot';

// Core Dashboard Imports
import Dashboard from './pages/Dashboard';
import AiInventory from './pages/AiInventory';
import RiskAnalysis from './pages/RiskAnalysis';
import ProgressTracker from './pages/ProgressTracker';
import Upgrade from './pages/Upgrade';
import SprintIntake from './pages/SprintIntake';
import AdminDashboard from './pages/AdminDashboard';

// Tier 2 & Enterprise Imports
import WorkflowMapper from './pages/WorkflowMapper';
import WorkflowCapabilityMapper from './pages/WorkflowCapabilityMapper';
import ExecutiveAlignment from './pages/ExecutiveAlignment';
import LlmReadinessGrader from './pages/LlmReadinessGrader';
import RoiDashboard from './pages/RoiDashboard';
import ExecutionRoadmap from './pages/ExecutionRoadmap';
import ExecutiveReadout from './pages/ExecutiveReadout';
import SecOpsDashboard from './pages/SecOpsDashboard';
import AiBomRegistry from './pages/AiBomRegistry';


// 1. Standard Protected Route (Any logged-in user)
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-white text-lg">Loading...</div>
    </div>
  );
  return user ? children : <Navigate to="/login" />;
}

// 2. Tier-Restricted Route Wrapper
function TierProtectedRoute({ children, requiredTier }) {
  const { user, loading } = useAuth();
  
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-white text-lg">Loading...</div>
    </div>
  );
  
  if (!user) return <Navigate to="/login" />;
  
  // If user tier is less than required, redirect to upgrade page
  if (user.tier < requiredTier) return <Navigate to="/upgrade" />;
  
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          
          {/* ------------------------------------------- */}
          {/* PUBLIC ROUTES                               */}
          {/* ------------------------------------------- */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          <Route path="/book-discovery" element={<BookDiscovery />} />
          <Route path="/tier-3-inquiry" element={<Tier3Inquiry />} />
          <Route path="/risk-grader" element={<AiRiskGrader />} />


          {/* ------------------------------------------- */}
          {/* PROTECTED SAAS ARCHITECTURE (GLOBAL LAYOUT) */}
          {/* ------------------------------------------- */}
          <Route element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            
            {/* Core / Tier 1 Routes */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/inventory" element={<AiInventory />} />
            <Route path="/risk-analysis" element={<RiskAnalysis />} />
            <Route path="/progress" element={<ProgressTracker />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/sprint-intake" element={<SprintIntake />} />
            <Route path="/admin" element={<AdminDashboard />} />

            {/* Dedicated AI Copilot Page */}
            <Route path="/governance-copilot" element={
              <div className="p-8 max-w-4xl mx-auto w-full">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">AI Governance Copilot</h1>
                <p className="text-slate-500 mb-8 text-sm">Ask jurisdictional questions and cross-reference global AI frameworks instantly.</p>
                <ComplianceChatbot />
              </div>
            } />

            {/* Tier 2 & Enterprise Routes (Moved INSIDE Layout) */}
            <Route path="/executive-alignment" element={<ExecutiveAlignment />} />
            <Route path="/capability-mapper" element={<WorkflowCapabilityMapper />} />
            <Route path="/secops" element={<SecOpsDashboard />} />
            <Route path="/roi-dashboard" element={<RoiDashboard />} />
            <Route path="/llm-readiness" element={<LlmReadinessGrader />} />
            <Route path="/roadmap" element={<ExecutionRoadmap />} />
            <Route path="/executive-readout" element={<ExecutiveReadout />} />
            <Route path="/aibom" element={<AiBomRegistry />} />

            <Route path="/workflow-mapping" element={
              <TierProtectedRoute requiredTier={2}>
                <WorkflowMapper />
              </TierProtectedRoute>
            } />

          </Route>

          {/* ------------------------------------------- */}
          {/* FALLBACK CATCH-ALL (MUST BE ABSOLUTE LAST)  */}
          {/* ------------------------------------------- */}
          <Route path="*" element={<Navigate to="/login" />} />

        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;